/**
 * Demo Database Loader
 *
 * Handles loading, caching, and resetting of demo SQLite databases.
 * Supports OPFS (primary) and IndexedDB (fallback) for persistence.
 */

import {
  isIndexedDbAvailable,
  getFromIndexedDb,
  saveToIndexedDb,
  deleteFromIndexedDb,
  clearIndexedDb,
} from './demoDatabaseLoaderInternal';

/** Storage size threshold for auto-checkpoint (25 MB) */
export const AUTO_CHECKPOINT_THRESHOLD = 25 * 1024 * 1024;

type LoadProgress = {
  phase: 'fetching' | 'parsing' | 'caching' | 'ready';
  progress: number; // 0-100
};

type LoadProgressCallback = (progress: LoadProgress) => void;

/**
 * Check if OPFS is available in the current browser.
 */
export async function isOpfsAvailable(): Promise<boolean> {
  try {
    const root = await navigator.storage.getDirectory();
    return root !== null;
  } catch {
    return false;
  }
}

// Re-export isIndexedDbAvailable for backward compatibility
export { isIndexedDbAvailable };

/**
 * Get the base URL for demo database assets.
 */
function getDemoAssetUrl(assetPath: string): string {
  const baseUrl = import.meta.env.BASE_URL || '/';
  return `${baseUrl}demo-databases/${assetPath}`;
}

/** Maximum retry attempts for fetch operations */
const MAX_FETCH_RETRIES = 3;

/** Delay between retry attempts (ms) */
const RETRY_DELAY = 1000;

/**
 * Fetch demo database from the server with retry support.
 */
async function fetchDemoDatabase(
  assetPath: string,
  onProgress?: LoadProgressCallback
): Promise<ArrayBuffer> {
  const url = getDemoAssetUrl(assetPath);
  console.log('[DemoLoader] Fetching from:', url);

  onProgress?.({ phase: 'fetching', progress: 0 });

  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= MAX_FETCH_RETRIES; attempt++) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch database: ${response.status} ${response.statusText}`);
      }

      return await streamResponse(response, onProgress);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown fetch error');
      console.warn(`[DemoLoader] Fetch attempt ${attempt}/${MAX_FETCH_RETRIES} failed:`, lastError.message);

      if (attempt < MAX_FETCH_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * attempt));
      }
    }
  }

  throw lastError || new Error('Failed to fetch database after retries');
}

/**
 * Stream response body with progress reporting.
 */
async function streamResponse(
  response: Response,
  onProgress?: LoadProgressCallback
): Promise<ArrayBuffer> {
  const contentLength = response.headers.get('content-length');
  const total = contentLength ? parseInt(contentLength, 10) : 0;

  if (!response.body) {
    // No streaming support, read all at once
    onProgress?.({ phase: 'fetching', progress: 100 });
    return response.arrayBuffer();
  }

  // Stream the response with progress reporting
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  let done = false;

  while (!done) {
    const result = await reader.read();
    done = result.done;

    if (done || !result.value) break;
    const value = result.value;

    chunks.push(value);
    received += value.length;

    if (total > 0) {
      const progress = Math.min(Math.round((received / total) * 100), 99);
      onProgress?.({ phase: 'fetching', progress });
    }
  }

  onProgress?.({ phase: 'fetching', progress: 100 });

  // Combine chunks into a single ArrayBuffer
  const combined = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.length;
  }

  return combined.buffer;
}

/**
 * Get the cache key for a database.
 */
function getCacheKey(databaseId: string, isModified: boolean = false): string {
  return isModified ? `${databaseId}:modified` : `${databaseId}:baseline`;
}

/**
 * Load a demo database, using cache if available.
 *
 * Loading strategy:
 * 1. Check for cached modified version first (if user made changes)
 * 2. Check for cached baseline version
 * 3. Fetch from server and cache baseline
 */
export async function loadDemoDatabase(
  databaseId: string,
  assetPath: string,
  onProgress?: LoadProgressCallback
): Promise<ArrayBuffer> {
  // Check for cached modified version first
  const modifiedKey = getCacheKey(databaseId, true);
  let cached = await getFromIndexedDb(modifiedKey);

  if (cached) {
    console.log('[DemoLoader] Loaded modified version from cache:', databaseId);
    onProgress?.({ phase: 'ready', progress: 100 });
    return cached;
  }

  // Check for cached baseline version
  const baselineKey = getCacheKey(databaseId, false);
  cached = await getFromIndexedDb(baselineKey);

  if (cached) {
    console.log('[DemoLoader] Loaded baseline from cache:', databaseId);
    onProgress?.({ phase: 'ready', progress: 100 });
    return cached;
  }

  // Fetch from server
  console.log('[DemoLoader] Fetching from server:', databaseId);
  const buffer = await fetchDemoDatabase(assetPath, onProgress);

  // Cache the baseline version
  onProgress?.({ phase: 'caching', progress: 0 });
  const cached_ok = await saveToIndexedDb(baselineKey, buffer);
  if (cached_ok) {
    console.log('[DemoLoader] Cached baseline:', databaseId);
  }
  onProgress?.({ phase: 'ready', progress: 100 });

  return buffer;
}

/**
 * Get the cached baseline bytes for a database.
 * Used for reset functionality.
 */
export async function getBaselineBytes(
  databaseId: string,
  assetPath: string
): Promise<ArrayBuffer> {
  const baselineKey = getCacheKey(databaseId, false);
  const cached = await getFromIndexedDb(baselineKey);

  if (cached) {
    return cached;
  }

  // Fetch and cache if not available
  const buffer = await fetchDemoDatabase(assetPath);
  await saveToIndexedDb(baselineKey, buffer);
  return buffer;
}

/**
 * Checkpoint (save) the current database state.
 * Only auto-checkpoints if size is below threshold.
 *
 * @returns true if checkpoint was successful, false otherwise
 */
export async function checkpointDatabase(
  databaseId: string,
  data: Uint8Array,
  force: boolean = false
): Promise<{ success: boolean; reason?: string }> {
  const size = data.byteLength;

  // Skip auto-checkpoint for large databases unless forced
  if (!force && size > AUTO_CHECKPOINT_THRESHOLD) {
    return {
      success: false,
      reason: 'Database too large for auto-checkpoint. Use Export to save changes.',
    };
  }

  const key = getCacheKey(databaseId, true);
  const success = await saveToIndexedDb(key, data.buffer);

  if (success) {
    console.log('[DemoLoader] Checkpointed database:', databaseId, `(${size} bytes)`);
    return { success: true };
  }

  return { success: false, reason: 'Failed to save to storage' };
}

/**
 * Reset a demo database to its baseline state.
 * Removes the modified version from cache.
 *
 * @returns The baseline bytes
 */
export async function resetDemoDatabase(
  databaseId: string,
  assetPath: string
): Promise<ArrayBuffer> {
  // Delete the modified version
  const modifiedKey = getCacheKey(databaseId, true);
  await deleteFromIndexedDb(modifiedKey);
  console.log('[DemoLoader] Deleted modified cache:', databaseId);

  // Return the baseline
  return getBaselineBytes(databaseId, assetPath);
}

/**
 * Check if a database has cached modifications.
 */
export async function hasCachedModifications(databaseId: string): Promise<boolean> {
  const key = getCacheKey(databaseId, true);
  const cached = await getFromIndexedDb(key);
  return cached !== null;
}

/**
 * Clear all cached demo databases.
 */
export async function clearDemoCache(): Promise<void> {
  await clearIndexedDb();
}

/**
 * Get storage availability status.
 */
export async function getStorageStatus(): Promise<{
  opfsAvailable: boolean;
  indexedDbAvailable: boolean;
  persistenceAvailable: boolean;
}> {
  const opfsAvailable = await isOpfsAvailable();
  const indexedDbAvailable = isIndexedDbAvailable();

  return {
    opfsAvailable,
    indexedDbAvailable,
    persistenceAvailable: opfsAvailable || indexedDbAvailable,
  };
}

/**
 * Get cached database bytes by ID.
 * Checks for modified version first, then baseline.
 * For uploaded databases, checks the upload cache.
 *
 * @returns The cached bytes, or null if not found
 */
export async function getCachedDatabase(databaseId: string): Promise<ArrayBuffer | null> {
  // Check for cached modified version first
  const modifiedKey = getCacheKey(databaseId, true);
  let cached = await getFromIndexedDb(modifiedKey);

  if (cached) {
    console.log('[DemoLoader] Found modified version in cache:', databaseId);
    return cached;
  }

  // Check for cached baseline version
  const baselineKey = getCacheKey(databaseId, false);
  cached = await getFromIndexedDb(baselineKey);

  if (cached) {
    console.log('[DemoLoader] Found baseline version in cache:', databaseId);
    return cached;
  }

  // Check for uploaded database cache (uses different key format)
  const uploadKey = `upload:${databaseId}`;
  cached = await getFromIndexedDb(uploadKey);

  if (cached) {
    console.log('[DemoLoader] Found uploaded database in cache:', databaseId);
    return cached;
  }

  return null;
}
